import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createSectionsWithPages, getSortedPages } from './Navbar.logic';
import { useAuthStore } from '@/stores';
import { useStanRouter } from '@/hooks/useStanRouter';
import { copyToClipboard, logWithSentry } from '@/lib';
import { User } from '@/types';

/**
 * Hook for managing window width and responding to resize events
 */
export const useWindowWidth = () => {
    const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);

    const updateWindowWidth = () => {
        setWindowWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener('resize', updateWindowWidth);
        return () => {
            window.removeEventListener('resize', updateWindowWidth);
        };
    }, []);

    return {
        windowWidth,
        isMobile: windowWidth <= 768,
        isSmallerThanTablet: windowWidth <= 1024,
    };
};

/**
 * Hook for managing URL display and clipboard functionality
 */
export const useUrlDisplay = (storeUrl: string, getPageURL?: string) => {
    const location = useLocation();
    const pathname = location.pathname;

    const displayUrl = useMemo(() => {
        if (pathname.includes('/linksite_edit_page') && getPageURL) {
            return getPageURL;
        }
        return storeUrl;
    }, [pathname, getPageURL, storeUrl]);

    const removeHttps = (url: string) => {
        return url.replace(/^https?:\/\//, '');
    };

    const copyTextToClipboard = async () => {
        try {
            await copyToClipboard(displayUrl, {
                successMessage: 'URL copied!',
                errorMessage: 'Failed to copy URL to clipboard',
                showNotifications: true,
            });
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    return {
        displayUrl,
        removeHttps,
        copyTextToClipboard,
    };
};

/**
 * Hook for managing navigation actions
 */
export const useNavigation = (
    showProductEditorPreview: boolean,
    setShowNavigation: (show: boolean) => void,
    setShowProductEditorPreview: (show: boolean) => void,
    storePreviewOverlayRef?: React.RefObject<any>,
    storePreviewAvailable?: boolean
) => {
    const { push, back } = useStanRouter();

    const closeMenu = () => {
        setShowNavigation(false);
    };

    const togglePreviewWithParam = (storePreviewAvailableParam: boolean) => {
        if (storePreviewAvailableParam) {
            closeMenu();
            storePreviewOverlayRef?.current?.toggleVisible();
        } else {
            setShowProductEditorPreview(!showProductEditorPreview);
        }
    };

    const togglePreview = () => {
        togglePreviewWithParam(storePreviewAvailable || false);
    };

    const goBack = () => {
        if (showProductEditorPreview) {
            togglePreviewWithParam(false);
        } else {
            back();
        }
    };

    const handleBreadcrumbNavigation = (
        tabName: string,
        query?: Record<string, string>,
        _params?: Record<string, string>
    ) => {
        // Add query parameters if they exist
        if (query && Object.keys(query).length > 0) {
            const searchParams = new URLSearchParams(query);
            tabName += `?${searchParams.toString()}`;
        }

        push(tabName);
    };

    return {
        closeMenu,
        togglePreview,
        goBack,
        handleBreadcrumbNavigation,
    };
};

/**
 * Hook for processing pages and sections data
 */
export const usePagesProcessing = (
    getStorePagesWithSections: any[],
    getStoreId: number,
    CONSTANTS?: any,
    helpers?: any
) => {
    const storePagesWithSections = useMemo(() => {
        if (!CONSTANTS?.PAGE_DEFAULTS) {
            return getStorePagesWithSections; // Return as-is if no constants available
        }
        return getSortedPages(getStorePagesWithSections, CONSTANTS.PAGE_DEFAULTS, helpers);
    }, [getStorePagesWithSections, CONSTANTS, helpers]);

    const sectionWithPages = useMemo(() => {
        return createSectionsWithPages(storePagesWithSections, getStoreId);
    }, [storePagesWithSections, getStoreId]);

    return {
        storePagesWithSections,
        sectionWithPages,
    };
};

/**
 * Hook for handling user status 0 logout
 */
export const useAuthActions = (user: User | undefined) => {
    const { push } = useStanRouter();
    const authLogout = useAuthStore(state => state.logout);

    useEffect(() => {
        if (user && user.status === '0') {
            authLogout()
                .catch(error => {
                    logWithSentry('logout-error', { context: { user_id: user.user_id, error } });
                })
                .finally(() => {
                    localStorage.clear();
                    push('/login');
                });
        }
    }, [user, authLogout, push]);
};
